// src/hooks/useSwNavigation.ts
// Riceve i messaggi `postMessage` dal Service Worker (notificationclick →
// tab focalizzato) e fa la navigazione SPA + mark-as-read (Fase 5.3 + 5.4).
//
// Senza questo hook montato in App, il postMessage del SW finisce nel
// vuoto e l'utente vede la pagina corrente invece del deep link.
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../api/axios';

interface SwClickMessage {
  type: 'hexa:notification-click';
  url?: string;
  notification_id?: number | string;
}

function isSwClickMessage(data: unknown): data is SwClickMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as any).type === 'hexa:notification-click'
  );
}

export function useSwNavigation() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      if (!isSwClickMessage(event.data)) return;
      const { url, notification_id } = event.data;

      // Fase 5.4 — mark-as-read viene fatto dal client perché dal SW non
      // abbiamo accesso a localStorage per il JWT. Best-effort, no blocking.
      if (notification_id !== undefined && notification_id !== null) {
        api
          .post(`api/notifications/notifications/${notification_id}/mark_as_read/`)
          .catch((err) => {
            console.warn('[useSwNavigation] mark_as_read fallito:', err);
          });
      }

      if (url && typeof url === 'string') {
        // Navigazione SPA. Se il path è esterno (raro), fallback hard-navigation.
        try {
          const isInternal = url.startsWith('/');
          if (isInternal) navigate(url);
          else window.location.href = url;
        } catch (err) {
          console.warn('[useSwNavigation] navigate fallita:', err);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [navigate]);
}
